import { create } from 'zustand';

/**
 * Drawing Store
 *
 * Manages freehand drawing strokes on the map canvas.
 * Local-only (no DB persistence, no realtime sync).
 */

export interface DrawingStroke {
  id: string;
  points: Array<{ x: number; y: number }>;
  color: number;
  width: number;
}

interface DrawingState {
  strokes: DrawingStroke[];
  currentStroke: { points: Array<{ x: number; y: number }>; color: number; width: number } | null;
  color: number;
  strokeWidth: number;

  // Actions
  startStroke: (x: number, y: number) => void;
  addPoint: (x: number, y: number) => void;
  endStroke: () => void;
  removeStroke: (id: string) => void;
  clearAll: () => void;
  undoLast: () => void;
  setColor: (color: number) => void;
  setStrokeWidth: (width: number) => void;
}

export const useDrawingStore = create<DrawingState>()((set, get) => ({
  strokes: [],
  currentStroke: null,
  color: 0xd4af37,
  strokeWidth: 3,

  startStroke: (x, y) => {
    const { color, strokeWidth } = get();
    set({
      currentStroke: {
        points: [{ x, y }],
        color,
        width: strokeWidth,
      },
    });
  },

  addPoint: (x, y) => {
    const { currentStroke } = get();
    if (!currentStroke) return;
    set({
      currentStroke: {
        ...currentStroke,
        points: [...currentStroke.points, { x, y }],
      },
    });
  },

  endStroke: () => {
    const { currentStroke, strokes } = get();
    if (!currentStroke || currentStroke.points.length < 2) {
      set({ currentStroke: null });
      return;
    }
    const stroke: DrawingStroke = {
      id: crypto.randomUUID(),
      points: currentStroke.points,
      color: currentStroke.color,
      width: currentStroke.width,
    };
    set({
      strokes: [...strokes, stroke],
      currentStroke: null,
    });
  },

  removeStroke: (id) => {
    set((state) => ({
      strokes: state.strokes.filter((s) => s.id !== id),
    }));
  },

  clearAll: () => {
    set({ strokes: [], currentStroke: null });
  },

  undoLast: () => {
    set((state) => ({
      strokes: state.strokes.slice(0, -1),
    }));
  },

  setColor: (color) => {
    set({ color });
  },

  setStrokeWidth: (width) => {
    set({ strokeWidth: width });
  },
}));
